'use client';

import React, { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { AlertCircle, CheckCircle2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { regionalizePath } from '@/lib/config/branches-config';
import { useLanguage } from '@/lib/config/useLanguage';
import { buildLeadThankYouUrl, clearPendingLeadSubmissionId, extractOptionalLeadId, getOrCreatePendingLeadSubmissionId } from '@/lib/forms/lead-thank-you';
import { useSmartCaptcha } from '@/components/SmartCaptcha';

import { trackLeadFormStage, trackLeadFormSuccess } from '@/lib/analytics/metrika';
import { useLeadFormAnalytics } from '@/lib/analytics/use-lead-form-analytics';

import { getLeadFormBranch } from '@/lib/forms/region';
import { formatRussianPhone, isValidRussianPhone } from '@/lib/forms/contact-validation';

const FORM_TYPE = 'project-calculation';

const CITY_OPTIONS = [
  { value: 'moscow-cfo', label: 'Москва (ЦФО)' },
  { value: 'saint-petersburg-szfo', label: 'Санкт-Петербург (СЗФО)' },
  { value: 'custom', label: 'Свой вариант' },
];

const PURPOSE_OPTIONS = [
  { value: 'private-construction', label: 'Частное строительство' },
  { value: 'industrial-construction', label: 'Промышленное строительство' },
  { value: 'standard-metalwork', label: 'Стандартизованные металлоизделия' },
  { value: 'custom-drawings', label: 'Металлоизделия по вашим чертежам' },
  { value: 'custom', label: 'Ваш вариант' },
];

const INSTALLATION_OPTIONS = [
  { value: 'yes', label: 'Да' },
  { value: 'no', label: 'Нет' },
];

const TIMELINE_OPTIONS = [
  { value: 'asap', label: 'Как можно скорее' },
  { value: '1-2-weeks', label: '1-2 недели' },
  { value: '2-4-weeks', label: '2-4 недели' },
  { value: '1-2-months', label: '1-2 месяца' },
  { value: '2-4-months', label: '2-4 месяца' },
];

const BUDGET_MIN = 10000;
const BUDGET_MAX = 100000000;
const SLIDER_MIN = 0;
const SLIDER_MAX = 100;

function sliderToBudget(position: number): number {
  const value = BUDGET_MIN * Math.pow(BUDGET_MAX / BUDGET_MIN, position / SLIDER_MAX);
  return Math.round(value);
}

function budgetToSlider(budget: number): number {
  if (budget <= BUDGET_MIN) return SLIDER_MIN;
  if (budget >= BUDGET_MAX) return SLIDER_MAX;
  return (Math.log(budget / BUDGET_MIN) / Math.log(BUDGET_MAX / BUDGET_MIN)) * SLIDER_MAX;
}

function formatBudget(value: number): string {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)} млн ₽`;
  }
  return `${value.toLocaleString('ru-RU')} ₽`;
}

const INITIAL_QUIZ = {
  city: '',
  cityCustom: '',
  purpose: '',
  purposeCustom: '',
  installation: '',
  timeline: '',
  budgetSlider: 0,
  budgetUseCustom: false,
  budgetCustom: '',
};

const INITIAL_CONTACT = {
  name: '',
  phone: '',
  email: '',
  message: '',
  privacy: false,
  marketing: false,
};

export default function ConstructionCalculationLeadForm({
  title,
  description,
  notice,
  variant = 'default',
}: {
  title: string;
  description: string;
  notice: string;
  variant?: 'default' | 'hero';
}) {
  const pathname = usePathname() || '/';
  const regionSearch = useSearchParams().toString();
  const router = useRouter();
  const analytics = useLeadFormAnalytics(FORM_TYPE);
  const { localizePath, langCode } = useLanguage();
  const privacyPolicyUrl = regionalizePath(localizePath('/pages/politika-konfidentsialnosti-personalnykh-dannykh'), 'spb');
  const [quizData, setQuizData] = useState(() => ({ ...INITIAL_QUIZ, city: getLeadFormBranch(pathname, regionSearch) === 'msk' ? 'moscow-cfo' : 'saint-petersburg-szfo' }));
  const [contactData, setContactData] = useState(INITIAL_CONTACT);
  const [drawingUrl, setDrawingUrl] = useState('');
  useEffect(() => {
    const branch = getLeadFormBranch(pathname, regionSearch);
    setQuizData((prev) => ({ ...prev, city: branch === 'msk' ? 'moscow-cfo' : 'saint-petersburg-szfo' }));
  }, [pathname, regionSearch]);
  const [contactError, setContactError] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { captchaElement, executeCaptcha, resetCaptcha, isCaptchaReady } = useSmartCaptcha();

  const isHeroVariant = variant === 'hero';
  const fieldClassName =
    'mt-1.5 flex h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-950 md:mt-2 md:text-sm';
  const textareaClassName =
    'mt-1.5 flex w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 md:mt-2 md:text-sm';
  const selectTriggerClassName = isHeroVariant
    ? 'mt-1.5 h-11 rounded-xl border-white/15 bg-white text-base text-slate-900 data-[placeholder]:text-slate-500 md:mt-2 md:text-sm'
    : 'mt-1.5 h-11 rounded-xl border-slate-200 bg-white text-base text-slate-900 md:mt-2 md:text-sm';

  const updateQuizField = (field: keyof typeof INITIAL_QUIZ, value: string) => {
    setContactError('');
    setSubmitStatus('idle');
    setQuizData((prev) => ({ ...prev, [field]: value }));
  };

  const updateContactField = (field: keyof typeof INITIAL_CONTACT, value: string | boolean) => {
    setContactError('');
    setSubmitStatus('idle');
    setContactData((prev) => ({ ...prev, [field]: field === 'phone' && typeof value === 'string' ? formatRussianPhone(value) : value }));
  };

  const getOptionLabel = (options: Array<{ value: string; label: string }>, value: string, customValue?: string) => {
    if (value === 'custom') {
      return customValue?.trim() || 'Не указано';
    }

    return options.find((option) => option.value === value)?.label || 'Не указано';
  };

  const renderSelectOptions = (options: Array<{ value: string; label: string }>) =>
    options.map((option) => (
      <SelectItem key={option.value} value={option.value}>
        {option.label}
      </SelectItem>
    ));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError('');
    setSubmitStatus('idle');

    if (!isValidRussianPhone(contactData.phone)) {
      trackLeadFormStage(FORM_TYPE, 'error', 'validation');
      setContactError('Укажите телефон в формате +7 (999) 123-45-67.');
      return;
    }
    if (!contactData.privacy) {
      trackLeadFormStage(FORM_TYPE, 'error', 'consent');
      setContactError('Необходимо согласие на обработку персональных данных.');
      return;
    }
    if (drawingUrl.trim() && !/^https?:\/\//i.test(drawingUrl.trim())) {
      setContactError('Укажите ссылку на чертежи, начинающуюся с https:// или http://.');
      return;
    }

    setIsSubmitting(true);

    const message = [
      'Заявка на расчёт конструкции',
      '',
      `Город: ${getOptionLabel(CITY_OPTIONS, quizData.city, quizData.cityCustom)}`,
      `Назначение: ${getOptionLabel(PURPOSE_OPTIONS, quizData.purpose, quizData.purposeCustom)}`,
      `Нужен ли монтаж: ${getOptionLabel(INSTALLATION_OPTIONS, quizData.installation)}`,
      `Срок: ${getOptionLabel(TIMELINE_OPTIONS, quizData.timeline)}`,
      `Бюджет: ${quizData.budgetUseCustom ? (quizData.budgetCustom.trim() || 'Не указано') : (quizData.budgetSlider > 0 ? formatBudget(quizData.budgetSlider) : 'Не определён')}`,
      '',
      `ФИО: ${contactData.name.trim() || 'Не указано'}`,
      `Email: ${contactData.email.trim() || 'Не указан'}`,
      '',
      `Ссылка на КМ/КМД: ${drawingUrl.trim() || 'Не указана'}`,
      'Комментарий клиента:',
      contactData.message.trim() || 'Без дополнительного комментария.',
    ].join('\n');

    try {
      const submissionId = getOrCreatePendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
      const smartToken = await executeCaptcha();
      const response = await fetch('/api/forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactData.name,
          phone: contactData.phone,
          email: contactData.email,
          message,
          privacy: contactData.privacy,
          marketing: contactData.marketing,
          formType: FORM_TYPE,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          locale: langCode,
          smartToken,
          submissionId,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        trackLeadFormSuccess(FORM_TYPE, submissionId);
        const thankYouUrl = buildLeadThankYouUrl({
          pathname: window.location.pathname,
          search: window.location.search,
          formType: FORM_TYPE,
          leadId: extractOptionalLeadId(data),
          sid: submissionId,
        });

        clearPendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
        router.push(thankYouUrl);
        return;
      } else {
        trackLeadFormStage(FORM_TYPE, 'error', 'server');
        setSubmitStatus('error');
        setSubmitMessage(data.message || 'Ошибка при отправке заявки.');
      }
    } catch (error) {
      trackLeadFormStage(FORM_TYPE, 'error', 'network-or-captcha');
      setSubmitStatus('error');
      setSubmitMessage('Не удалось отправить заявку. Попробуйте ещё раз.');
    } finally {
      resetCaptcha();
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="calculator"
      className={
        isHeroVariant
          ? 'rounded-[24px] border border-white/15 bg-white/10 p-3.5 backdrop-blur-sm md:rounded-[28px] md:p-6'
          : 'mt-8 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm md:mt-10 md:rounded-[32px] md:p-8'
      }
    >
      <div>
        {!isHeroVariant && (
          <>
            <div className="inline-flex rounded-full bg-red-50 px-3 py-1 text-sm font-medium text-red-600">
              Заявка на расчёт
            </div>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">{title}</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">{description}</p>
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {notice}
            </div>
          </>
        )}

        <form {...analytics} onSubmit={handleSubmit} className={`mt-5 space-y-4 md:mt-8 md:space-y-6 ${isHeroVariant ? 'text-white' : ''}`}>
          <div className="grid gap-4 md:gap-6 md:grid-cols-2">
            <div>
              <Label htmlFor="name">ФИО</Label>
              <Input
                id="name"
                value={contactData.name}
                onChange={(e) => updateContactField('name', e.target.value)}
                className={fieldClassName}
                placeholder="Иванов Иван Иванович"
              />
            </div>
            <div>
              <Label htmlFor="phone">
                Телефон <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                value={contactData.phone}
                onChange={(e) => updateContactField('phone', e.target.value)}
                className={fieldClassName}
                placeholder="+7 (999) 123-45-67"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={contactData.email}
                onChange={(e) => updateContactField('email', e.target.value)}
                className={fieldClassName}
                placeholder="example@mail.ru"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="message">Комментарий</Label>
              <textarea
                id="message"
                value={contactData.message}
                onChange={(e) => updateContactField('message', e.target.value)}
                rows={4}
                className={textareaClassName}
                placeholder="Опишите объект, размеры, сроки или любые пожелания по расчёту."
              />
            </div>
          </div>

          <div>
            <Label htmlFor="drawing-url">Ссылка на КМ/КМД или техническое задание (необязательно)</Label>
            <Input id="drawing-url" type="url" value={drawingUrl} onChange={(e) => setDrawingUrl(e.target.value)} className={fieldClassName} placeholder="https://…" />
            <p className="mt-2 text-sm">Можно указать ссылку на файл в облаке или передать чертежи после звонка.</p>
          </div>
          <details className="rounded-xl border border-slate-200/30 p-4">
            <summary className="cursor-pointer font-medium">Добавить параметры проекта (необязательно)</summary>
            <div className="mt-4">
          <div className="grid gap-3 md:gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="citySelect">1. В каком городе требуются металлоконструкции?</Label>
              <Select value={quizData.city} onValueChange={(value) => updateQuizField('city', value)}>
                <SelectTrigger id="citySelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите город">{quizData.city ? getOptionLabel(CITY_OPTIONS, quizData.city, quizData.cityCustom) : undefined}</SelectValue>
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(CITY_OPTIONS)}</SelectContent>
              </Select>
              {quizData.city === 'custom' && (
                <div className="mt-3 md:mt-4">
                  <Label htmlFor="cityCustom">Укажите город</Label>
                  <Input
                    id="cityCustom"
                    value={quizData.cityCustom}
                    onChange={(e) => updateQuizField('cityCustom', e.target.value)}
                    className={fieldClassName}
                    placeholder="Например, Екатеринбург"
                  />
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="purposeSelect">2. Какого назначения требуется металлоконструкция?</Label>
              <Select value={quizData.purpose} onValueChange={(value) => updateQuizField('purpose', value)}>
                <SelectTrigger id="purposeSelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите назначение" />
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(PURPOSE_OPTIONS)}</SelectContent>
              </Select>
              {quizData.purpose === 'custom' && (
                <div className="mt-3 md:mt-4">
                  <Label htmlFor="purposeCustom">Укажите назначение</Label>
                  <Input
                    id="purposeCustom"
                    value={quizData.purposeCustom}
                    onChange={(e) => updateQuizField('purposeCustom', e.target.value)}
                    className={fieldClassName}
                    placeholder="Опишите ваш вариант"
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="installationSelect">3. Нужен ли монтаж?</Label>
              <Select value={quizData.installation} onValueChange={(value) => updateQuizField('installation', value)}>
                <SelectTrigger id="installationSelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите вариант" />
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(INSTALLATION_OPTIONS)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="timelineSelect">4. В какой срок хотите уложиться?</Label>
              <Select value={quizData.timeline} onValueChange={(value) => updateQuizField('timeline', value)}>
                <SelectTrigger id="timelineSelect" className={selectTriggerClassName}>
                  <SelectValue placeholder="Выберите срок" />
                </SelectTrigger>
                <SelectContent>{renderSelectOptions(TIMELINE_OPTIONS)}</SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label>Бюджет (необязательно)</Label>
              <p className="mt-2 text-sm">{quizData.budgetSlider === 0 && !quizData.budgetUseCustom ? 'Пока не определён — уточним при расчёте' : 'Указан вами'}</p>
              {!quizData.budgetUseCustom && (
                <div className="mt-3 md:mt-4">
                  <div className="mb-3 flex justify-center">
                    <span className="inline-block rounded-full bg-white px-5 py-1.5 text-lg font-semibold text-slate-900 shadow-sm">
                      {quizData.budgetSlider > 0 ? formatBudget(quizData.budgetSlider) : 'Пока не определён'}
                    </span>
                  </div>
                  <input
                    type="range"
                    aria-label="Бюджет"
                    min={SLIDER_MIN}
                    max={SLIDER_MAX}
                    step={0.1}
                    value={budgetToSlider(quizData.budgetSlider)}
                    onChange={(e) => {
                      const sliderValue = parseFloat(e.target.value);
                      const budget = sliderToBudget(sliderValue);
                      setQuizData((prev) => ({ ...prev, budgetSlider: budget }));
                      setContactError('');
                      setSubmitStatus('idle');
                    }}
                    className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-slate-200 accent-red-600 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-red-600 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-600"
                  />
                  <div className="mt-1 flex justify-between text-xs text-slate-500">
                    <span>10 000 ₽</span>
                    <span>100 млн ₽</span>
                  </div>
                </div>
              )}
              <div className="mt-3 flex items-center md:mt-4">
                <Checkbox
                  id="budgetUseCustom"
                  checked={quizData.budgetUseCustom}
                  onCheckedChange={(checked) => {
                    setQuizData((prev) => ({ ...prev, budgetUseCustom: checked === true }));
                    setContactError('');
                    setSubmitStatus('idle');
                  }}
                  className={isHeroVariant ? 'border-white bg-white text-slate-900' : ''}
                />
                <Label htmlFor="budgetUseCustom" className={`ml-2 text-sm ${isHeroVariant ? 'text-white/75' : 'text-slate-600'}`}>
                  Ваш вариант
                </Label>
              </div>
              {quizData.budgetUseCustom && (
                <div className="mt-3 md:mt-4">
                  <Label htmlFor="budgetCustom">Укажите бюджет</Label>
                  <Input
                    id="budgetCustom"
                    value={quizData.budgetCustom}
                    onChange={(e) => updateQuizField('budgetCustom', e.target.value)}
                    className={fieldClassName}
                    placeholder="Например, до 2 млн р"
                  />
                </div>
              )}
            </div>
          </div>

            </div>
          </details>

          <div className="space-y-3 md:space-y-4">
            <div className="flex items-start">
              <Checkbox
                id="privacy"
                checked={contactData.privacy}
                onCheckedChange={(checked) => updateContactField('privacy', checked === true)}
                className={isHeroVariant ? 'mt-1 border-white bg-white text-slate-900' : 'mt-1'}
              />
              <Label htmlFor="privacy" className={`ml-2 text-sm leading-5 md:leading-6 ${isHeroVariant ? 'text-white/75' : 'text-slate-600'}`}>
                Я даю согласие на обработку персональных данных и соглашаюсь с{' '}
                <a href={privacyPolicyUrl} className="text-red-600 hover:underline">
                  политикой конфиденциальности
                </a>
                .
              </Label>
            </div>
            <div className="flex items-start">
              <Checkbox
                id="marketing"
                checked={contactData.marketing}
                onCheckedChange={(checked) => updateContactField('marketing', checked === true)}
                className={isHeroVariant ? 'mt-1 border-white bg-white text-slate-900' : 'mt-1'}
              />
              <Label htmlFor="marketing" className={`ml-2 text-sm leading-5 md:leading-6 ${isHeroVariant ? 'text-white/75' : 'text-slate-600'}`}>
                Я даю согласие на направление рекламных рассылок (необязательно).
              </Label>
            </div>
          </div>

          {contactError && (
            <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {contactError}
            </div>
          )}

          {submitStatus === 'success' && (
            <div className="flex items-center gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {submitMessage}
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {submitMessage}
            </div>
          )}

          {captchaElement}

          <div className="flex justify-end">
            <Button type="submit" className="bg-red-600 text-white hover:bg-red-700" disabled={!isCaptchaReady || isSubmitting}>
              {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              {!isSubmitting && <Send className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
